import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import ButtonBase from '@material-ui/core/ButtonBase';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import LazyAnimation from './LazyAnimation';

const useStyles = makeStyles(theme => ({
  wrapper: {
    backgroundColor: '#FFFFFF',
    paddingTop: 95,
    paddingBottom: 83,
  },
  container: {
    width: '80%',
    maxWidth: '89%',
    margin: 'auto',
  },
  header: {
    fontFamily: 'PoppinsSemiBold',
    fontWeight: '600',
    fontSize: 26,
    lineHeight: '1.5em',
    paddingBottom: 10,
    color: '#333333',
    marginBottom: 15,
  },
  body: {
    fontSize: 16,
    lineHeight: '1.8em',
    fontFamily: 'OpenSansRegular',
    color: '#666666',
  },
  list: {
    padding: 0,
    paddingLeft: 16,
    listStyleType: 'disc',
    fontSize: 16,
    color: '#666666',
    '& p': {
      fontSize: 16,
      color: '#666666',
      lineHeight: '26px',
      fontFamily: 'OpenSansRegular',
    },
  },
  buttonWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  button: {
    color: '#FFFFFF',
    borderRadius: 3,
    letterSpacing: 2,
    fontSize: 16,
    fontFamily: 'MontserratSemiBold',
    fontWeight: '600',
    textTransform: 'uppercase',
    textAlign: 'center',
    backgroundColor: '#E09900',
    lineHeight: '1.7em',
    padding: '12px 1.5em',
    transition: 'all 300ms ease 0ms',
    position: 'relative',
    '& > svg': {
      position: 'absolute',
      opacity: 0,
      marginLeft: '-1em',
      transition: 'all 300ms ease 0ms',
      height: 25,
      right: 8,
    },
    '&:hover': {
      paddingRight: '2.5em',
      paddingLeft: '1.2em',
      borderRadius: 25,
      '& > svg': {
        opacity: 1,
        marginLeft: 0,
      },
    },
  },
  imageWrapper: {
    width: '90%',
    marginBottom: '5%',
    marginLeft: 'auto',
    marginRight: 'auto',
    [theme.breakpoints.down('sm')]: {
      marginBottom: 30,
    },
  },
  image: {
    maxWidth: '100%',
    height: 'auto',
    boxShadow: '0px 2px 18px 0px rgba(0,0,0,0.3)',
  },
  themesContainer: {
    position: 'relative',
  },
}));

function AppSection({ onClick }) {
  const classes = useStyles();

  const bullets = [
    'Co-branding for you to project a leadership position in your community.',
    'Links to resources you’ve invested in to help consumers find local resources, such as professional services.',
    'Community reports that let you see the “mental health profile” of your community, areas of need, and information to plan more effective interventions.',
    'Micro-targeting to support specific audiences, such as youth, seniors, those recently incarcerated, and more.',
  ];

  return (
    <section id="app-section">
      <div className={classes.wrapper}>
        <div className={classes.container}>
          <Grid container spacing={8}>
            <Grid item md={6}>
              <LazyAnimation type="translate-right-40">
                <Typography className={classes.header}>
                  Highly Customizable to Reflect <b>Your</b> Strategies and
                  Services
                </Typography>
              </LazyAnimation>
              <LazyAnimation type="translate-right-40">
                <Typography className={classes.body}>
                  Here’s where you can make CredibleMind your own through these
                  core features:
                </Typography>
                <ul className={classes.list}>
                  {bullets.map(bullet => (
                    <li key={bullet}>
                      <Typography>{bullet}</Typography>
                    </li>
                  ))}
                </ul>
                <Typography className={classes.body}>
                  Over time we’ll offer a knowledge base of “what works” by
                  measuring the outcomes of resources used by individuals and
                  identifying those that are most effective.
                </Typography>
              </LazyAnimation>
            </Grid>
            <Grid item md={6}>
              <div className={classes.imageWrapper}>
                <LazyAnimation type="translate-left-200 fade-in">
                  <img
                    className={classes.image}
                    src="https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/customize-2.png?w=1080&amp;ssl=1"
                    alt=""
                    title=""
                    srcSet="https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/customize-2.png?w=1571&amp;ssl=1 1571w, https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/customize-2.png?resize=300%2C184&amp;ssl=1 300w, https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/customize-2.png?resize=1024%2C630&amp;ssl=1 1024w, https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/customize-2.png?resize=768%2C472&amp;ssl=1 768w, https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/customize-2.png?resize=1536%2C944&amp;ssl=1 1536w, https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/customize-2.png?resize=1080%2C664&amp;ssl=1 1080w, https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/customize-2.png?resize=1280%2C787&amp;ssl=1 1280w, https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/customize-2.png?resize=980%2C603&amp;ssl=1 980w, https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/customize-2.png?resize=480%2C295&amp;ssl=1 480w"
                    sizes="(max-width: 1080px) 100vw, 1080px"
                    data-recalc-dims="1"
                  />
                </LazyAnimation>
              </div>
              <div className={classes.buttonWrapper}>
                <LazyAnimation type="translate-left-40 fade-in">
                  <ButtonBase
                    disableRipple
                    onClick={() => onClick('cta', 'app-section')}
                    classes={{ root: classes.button }}
                  >
                    Launch yours in 15 days
                    <ChevronRightIcon />
                  </ButtonBase>
                </LazyAnimation>
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
    </section>
  );
}

AppSection.propTypes = {
  onClick: PropTypes.func,
};

export default AppSection;
